/**
 * DatetimeStep — calendar + time-slot picker + slot reservation +
 * "no slots? join the waitlist" empty state. The lynchpin of the
 * booking flow: this is where customers commit to a specific time
 * and we put a 10-minute hold on it so nobody else can race them.
 *
 * Lifted state (parent owns):
 *   - date + setDate, time + setTime — the eventual appointment
 *     coordinates. Parent ships these to /api/appointments on submit.
 *   - slotReservation — the useSlotReservation() hook return. Same
 *     hook instance is shared with the deposit confirm step so the
 *     reservation banner is consistent across the flow.
 *
 * Local state (component owns):
 *   - calMonth + calYear — calendar pagination, doesn't matter to
 *     parent.
 *   - availableSlots, loadingSlots, slotsError — fetched from
 *     /api/appointments/available-slots whenever date or
 *     appointmentType changes.
 *
 * Settings come from the parent because the parent's side panel uses
 * the same /api/appointments/settings response (bookingWindowDays +
 * holidayExtensionDates + business hours). One fetch, two consumers.
 *
 * Extracted from consultation/page.tsx — Phase 1.5 of booking
 * consolidation (wiki/plans/booking-consolidation-2026-04-28.md).
 */
"use client";

import { useCallback, useEffect, useMemo, useState, type ComponentType, type SVGProps } from "react";
import {
  AlertTriangle,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Info,
  Mail,
  Phone,
  Scissors,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useToast } from "@/context/ToastContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { phoneToTelHref, phoneToSmsHref } from "@/lib/phone";
import { trackEvent } from "@/lib/track";
import { cn } from "@/lib/utils";
import { studioToday } from "@/lib/studio-tz";
import {
  allowedTypesForDay,
  dayAllowanceLabel,
  typeAllowedOnDay,
} from "@/lib/appointment-day-rules";
import WaitlistCard from "@/components/booking/WaitlistCard";

interface AvailableSlot {
  time: string;
  available: boolean;
}

interface SlotReservationLike {
  reservation: { expiresAt: string } | null;
  reserve: (input: {
    date: string;
    startTime: string;
    type: string;
  }) => Promise<unknown>;
  markExpired: () => void;
}

interface DatetimeStepProps {
  /** Appointment type slug (selectedService || selectedType from parent). */
  appointmentType: string;
  /**
   * Extra services riding on the same visit (wash + color). Sent to the
   * slots API so it only offers times long enough for the whole visit —
   * the booking POST sums all durations when it re-checks capacity.
   */
  additionalTypes?: string[];
  /**
   * Inline notice shown above the time grid, e.g. after the time the
   * customer picked was taken by the time they hit Confirm.
   */
  notice?: string | null;
  /** Current picked ISO date "YYYY-MM-DD". */
  date: string;
  setDate: (d: string) => void;
  /** Current picked time, e.g. "10:30 AM". */
  time: string;
  setTime: (t: string) => void;
  /** Same useSlotReservation() result the parent uses elsewhere. */
  slotReservation: SlotReservationLike;
  /** Days-ahead cap from AppointmentSettings. */
  bookingWindowDays: number;
  /**
   * Server-computed holiday extension dates (Pesach week opened 90
   * days out, etc.). Selectable even outside the normal window.
   */
  holidayExtensionDates: Set<string>;
  /**
   * Weekly closed days (0=Sun … 6=Sat) from
   * AppointmentSettings.closedDays. Mirrors the server's slot check so
   * a permanently-closed day is greyed out instead of answering
   * "Closed day" only after the customer taps it.
   */
  closedDays?: number[];
  /**
   * Per-weekday service restriction map from
   * AppointmentSettings.allowedTypesByDay (e.g. Sundays are
   * consultations + repairs only). Greys out days that can't take the
   * currently-selected service.
   */
  allowedTypesByDay?: Record<string, string[]>;
  /** Header label for the "what's being scheduled" tag (e.g. "In-store Consult"). */
  primaryLabel?: string;
  primaryIcon?: ComponentType<SVGProps<SVGSVGElement>>;
  /** Secondary header label (the salon service when both are picked). */
  secondaryLabel?: string;
  /** Contact identity passed to the waitlist API on no-slots-available. */
  name: string;
  email: string;
  phone: string;
  /**
   * Days the customer must drop the wig off in advance, per
   * AppointmentSettings.dropoffDaysByType. 0 (or undefined) = no
   * drop-off reminder banner. Parent computes this from settings —
   * see UnifiedBookPage's settings fetch.
   *
   * Replaces the legacy `showDropoffReminder: boolean` prop. The
   * banner copy now reads the days count directly so wash_set says
   * "drop off 2 days before" while color says "drop off 3 days
   * before" without changing the JSX.
   */
  dropoffDays?: number;
  /**
   * trackEvent name for date selection. Defaults to legacy
   * "consultation_date_selected" so analytics history is preserved.
   */
  trackDateEventName?: string;
  /**
   * trackEvent name for time selection. Defaults to legacy
   * "consultation_time_selected".
   */
  trackTimeEventName?: string;
  /** i18n namespace. Defaults to "consultation". Phase 2 will rename. */
  i18nNamespace?: string;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Module-level so the default prop value is referentially stable and
// doesn't retrigger the legend useMemo on every render.
const NO_CLOSED_DAYS: number[] = [];
const NO_TYPE_RESTRICTIONS: Record<string, string[]> = {};

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

function formatDateStr(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatDisplayDate(dateStr: string) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function DatetimeStep({
  appointmentType,
  additionalTypes,
  notice,
  date,
  setDate,
  time,
  setTime,
  slotReservation,
  bookingWindowDays,
  holidayExtensionDates,
  closedDays = NO_CLOSED_DAYS,
  allowedTypesByDay = NO_TYPE_RESTRICTIONS,
  primaryLabel,
  primaryIcon: PrimaryIcon,
  secondaryLabel,
  name,
  email,
  phone,
  dropoffDays,
  trackDateEventName = "consultation_date_selected",
  trackTimeEventName = "consultation_time_selected",
  i18nNamespace = "consultation",
}: DatetimeStepProps) {
  const t = useTranslations(i18nNamespace);
  const { toast } = useToast();
  const { supportPhone, voicePhone } = useSiteConfig();

  // Studio "today" — the booking calendar is NY-based; the visitor's
  // local date (Israel after midnight, or the UTC SSR pass) would
  // highlight the wrong "today" / open on the wrong month.
  const today = useMemo(() => {
    const [y, m, d] = studioToday().split("-").map(Number);
    return { year: y, month: m - 1, date: d };
  }, []);
  const [calMonth, setCalMonth] = useState(today.month);
  const [calYear, setCalYear] = useState(today.year);

  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  // Two distinct empty states. `noSlots` is a business rule — the day is
  // closed, restricted to other services, or fully booked — and reads as
  // calm amber. `slotsError` is a genuine network/500 failure and stays
  // red. Collapsing both into one red "No available slots" made a normal
  // closed Sunday look like the site was broken (Rivky, 2026-07-24).
  const [noSlots, setNoSlots] = useState(false);
  const [closedReason, setClosedReason] = useState<string | null>(null);
  const [slotsError, setSlotsError] = useState(false);
  // Inline "that time was just taken" message. Rivky (2026-10-17):
  // customers tried to book, the time wasn't available, and all they
  // got was a toast that vanished (or nothing), so they thought the
  // site was broken. Kept on screen until they pick another time.
  const [takenNotice, setTakenNotice] = useState<string | null>(notice ?? null);
  useEffect(() => {
    if (notice) setTakenNotice(notice);
  }, [notice]);
  const additionalKey = (additionalTypes ?? []).join(",");

  // Fetch available slots whenever date or type changes. Type is part
  // of the request because slot duration varies (wash-set 15min, new
  // wig 45min) and the server sizes the grid accordingly.
  const fetchSlots = useCallback(
    async (selectedDate: string, apptType: string, extras: string) => {
      if (!selectedDate) {
        setAvailableSlots([]);
        return;
      }
      setLoadingSlots(true);
      setSlotsError(false);
      setNoSlots(false);
      setClosedReason(null);
      setTime("");
      try {
        const qs = new URLSearchParams({ date: selectedDate });
        if (apptType) qs.set("type", apptType);
        if (extras) qs.set("additionalTypes", extras);
        const res = await fetch(
          `/api/appointments/available-slots?${qs.toString()}`
        );
        if (!res.ok) throw new Error("Failed to fetch slots");
        const data = await res.json();
        const slots: AvailableSlot[] = Array.isArray(data.slots)
          ? data.slots
          : [];
        setAvailableSlots(slots);
        if (data.closed || slots.length === 0) {
          setNoSlots(true);
          // The server explains exactly why ("Shabbos / Yom Tov", "On
          // Sundays we only take…"). Surfacing it is the whole point —
          // the customer previously got a generic string and assumed
          // the booking form was broken.
          setClosedReason(
            typeof data.reason === "string" && data.reason ? data.reason : null
          );
        }
      } catch {
        setAvailableSlots([]);
        setSlotsError(true);
      } finally {
        setLoadingSlots(false);
      }
    },
    [setTime]
  );

  useEffect(() => {
    if (date) {
      fetchSlots(date, appointmentType, additionalKey);
    }
  }, [date, appointmentType, additionalKey, fetchSlots]);

  // ─── Calendar navigation ───
  const goToPrevMonth = () => {
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear(calYear - 1);
    } else {
      setCalMonth(calMonth - 1);
    }
  };
  const goToNextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear(calYear + 1);
    } else {
      setCalMonth(calMonth + 1);
    }
  };
  const isPastMonth = () =>
    calYear < today.year ||
    (calYear === today.year && calMonth < today.month);
  const isDateSelectable = (year: number, month: number, day: number) => {
    // Compare YYYY-MM-DD strings in STUDIO terms (matches the server's
    // lib/booking-window). Visitor-local Date math let e.g. an Israeli
    // customer after her midnight lose a bookable day, and desynced
    // from the server validation.
    const dayOfWeek = new Date(year, month, day).getDay();
    if (dayOfWeek === 6) return false; // Shabbat
    // Weekly closed days + per-day service restrictions, mirroring
    // /api/appointments/available-slots. Without these the calendar
    // offered Fridays (always "Closed day") and offered every Sunday
    // for wash & set (always "Sundays are consultations + repairs
    // only") — the customer only found out after picking the date.
    if (closedDays.includes(dayOfWeek)) return false;
    if (
      appointmentType &&
      !typeAllowedOnDay(allowedTypesByDay, dayOfWeek, appointmentType)
    ) {
      return false;
    }
    const key = formatDateStr(year, month, day);
    const todayKey = studioToday();
    if (key <= todayKey) return false; // next-day minimum
    // Max = studio-today + window; noon-UTC pin avoids DST day-drift.
    const max = new Date(
      new Date(`${todayKey}T12:00:00Z`).getTime() +
        bookingWindowDays * 24 * 60 * 60 * 1000
    );
    if (key <= max.toISOString().slice(0, 10)) return true;
    return holidayExtensionDates.has(key);
  };

  // Legend for the greyed-out days — a customer who sees every Sunday
  // dimmed needs to know it's a service restriction, not an outage.
  const restrictionLegend = useMemo(() => {
    const lines: string[] = [];
    for (let d = 0; d <= 6; d++) {
      if (d === 6 || closedDays.includes(d)) continue;
      const allowed = allowedTypesForDay(allowedTypesByDay, d);
      if (allowed) lines.push(dayAllowanceLabel(d, allowed));
    }
    return lines;
  }, [closedDays, allowedTypesByDay]);

  const DAY_LABELS = [
    t("sun"),
    t("mon"),
    t("tue"),
    t("wed"),
    t("thu"),
    t("fri"),
    t("sat"),
  ];

  return (
    <div className="animate-fade-in">
      <h2 className="font-display text-2xl text-brand-dark mb-2">
        {t("scheduleAppointment")}
      </h2>
      <p className="text-sm text-brand-muted mb-4">
        {primaryLabel}
        {secondaryLabel && (
          <span className="inline-flex items-center gap-1 ms-2 text-brand-gold">
            <Scissors className="h-3.5 w-3.5" />
            {secondaryLabel}
          </span>
        )}
        {PrimaryIcon && (
          <span className="inline-flex items-center gap-1 ms-2 text-brand-gold">
            <PrimaryIcon className="h-3.5 w-3.5" />
          </span>
        )}
      </p>

      {/* Drop-off reminder — salon services that need the wig in hand
          before the appointment. Day count is admin-configurable per
          service type via AppointmentSettings.dropoffDaysByType. */}
      {dropoffDays !== undefined && dropoffDays > 0 && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/50 p-3 flex items-start gap-2">
          <Scissors className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900 leading-relaxed">
            Please drop off the wig at the studio at least{" "}
            <strong>
              {dropoffDays} {dropoffDays === 1 ? "day" : "days"} before
            </strong>{" "}
            your appointment so Rivky has time to work on it.
            You&apos;ll get a reminder SMS when it&apos;s time.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Calendar */}
        <div>
          <label className="mb-3 block text-xs font-medium uppercase tracking-widest text-brand-muted">
            {t("selectDate")}
          </label>
          <div className="border border-gray-100 bg-white p-4 sm:p-5" dir="ltr">
            {/* Month nav — always LTR so calendar reads naturally */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={goToPrevMonth}
                disabled={isPastMonth()}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4 text-brand-dark" />
              </button>
              <h3 className="font-display text-base text-brand-dark">
                {MONTH_NAMES[calMonth]} {calYear}
              </h3>
              <button
                onClick={goToNextMonth}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronRight className="h-4 w-4 text-brand-dark" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1">
              {DAY_LABELS.map((d) => (
                <div
                  key={d}
                  className="text-center text-[11px] font-medium uppercase tracking-wider text-brand-muted py-1"
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
              {/* Empty cells for first week offset */}
              {Array.from({ length: getFirstDayOfWeek(calYear, calMonth) }).map(
                (_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                )
              )}
              {/* Day cells */}
              {Array.from({ length: getDaysInMonth(calYear, calMonth) }).map(
                (_, i) => {
                  const day = i + 1;
                  const dateStr = formatDateStr(calYear, calMonth, day);
                  const selectable = isDateSelectable(calYear, calMonth, day);
                  const isSelected = date === dateStr;
                  const isToday =
                    day === today.date &&
                    calMonth === today.month &&
                    calYear === today.year;

                  return (
                    <button
                      key={day}
                      onClick={() => {
                        if (selectable) {
                          if (dateStr !== date) setTakenNotice(null);
                          setDate(dateStr);
                          trackEvent(trackDateEventName, { date: dateStr });
                        }
                      }}
                      disabled={!selectable}
                      className={cn(
                        "aspect-square p-1 sm:p-2 flex items-center justify-center text-xs sm:text-sm rounded-full transition-all duration-200 relative",
                        selectable
                          ? "hover:bg-brand-gold/10 cursor-pointer"
                          : "text-gray-300 cursor-not-allowed",
                        isSelected
                          ? "bg-brand-gold text-white font-medium shadow-md hover:bg-brand-gold-dark"
                          : selectable
                            ? "text-brand-dark"
                            : "",
                        isToday && !isSelected && "font-bold"
                      )}
                    >
                      {day}
                      {isToday && !isSelected && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-brand-gold" />
                      )}
                    </button>
                  );
                }
              )}
            </div>
          </div>

          {restrictionLegend.length > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-gray-100 bg-brand-cream/30 p-3">
              <Info className="h-3.5 w-3.5 text-brand-gold shrink-0 mt-0.5" />
              <div className="text-xs text-brand-muted leading-relaxed">
                {restrictionLegend.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Time slots */}
        <div>
          <label className="mb-3 block text-xs font-medium uppercase tracking-widest text-brand-muted">
            {t("selectTimeSlot")}
          </label>

          {takenNotice && (
            <div
              role="alert"
              className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3"
            >
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">{takenNotice}</p>
            </div>
          )}

          {!date ? (
            <div className="border border-gray-100 bg-white p-8 flex flex-col items-center justify-center text-center min-h-[300px]">
              <CalendarIcon className="h-10 w-10 text-gray-200 mb-3" />
              <p className="text-sm text-brand-muted">{t("chooseDate")}</p>
            </div>
          ) : loadingSlots ? (
            <div className="border border-gray-100 bg-white p-8 min-h-[300px]">
              <div className="h-4 w-40 bg-brand-dark/5 rounded animate-pulse mb-5" />
              <div className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-12 bg-brand-dark/5 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            </div>
          ) : slotsError || noSlots ? (
            <div
              className={cn(
                "border p-6 sm:p-8 flex flex-col items-center justify-center text-center min-h-[300px]",
                slotsError
                  ? "border-red-100 bg-red-50/40"
                  : "border-amber-200 bg-amber-50/40"
              )}
            >
              {slotsError ? (
                <AlertTriangle className="h-10 w-10 text-red-300 mb-3" />
              ) : (
                <Clock className="h-10 w-10 text-amber-400 mb-3" />
              )}
              <p
                className={cn(
                  "text-sm font-medium mb-1",
                  slotsError ? "text-red-600" : "text-amber-900"
                )}
              >
                {slotsError
                  ? t("slotsLoadFailed")
                  : closedReason || t("noSlotsAvailable")}
              </p>
              <p
                className={cn(
                  "text-xs mb-4",
                  slotsError ? "text-red-500" : "text-amber-800"
                )}
              >
                {slotsError
                  ? t("slotsLoadFailedHint")
                  : t("noSlotsAvailableHint")}
              </p>
              <div className="mb-4 flex flex-col sm:flex-row gap-2 w-full max-w-sm">
                <a
                  href={phoneToTelHref(voicePhone)}
                  className="flex-1 rounded-lg border border-brand-gold px-4 py-2.5 text-sm font-medium text-brand-gold hover:bg-brand-gold hover:text-white transition-colors flex items-center justify-center gap-2"
                >
                  <Phone className="h-4 w-4" />
                  Call Rivky
                </a>
                <a
                  href={phoneToSmsHref(
                    supportPhone,
                    `Hi Rivky, I'd like to book an appointment${date ? ` around ${date}` : ""} but don't see a time that works. Can you accommodate me?`
                  )}
                  className="flex-1 rounded-lg border border-brand-gold px-4 py-2.5 text-sm font-medium text-brand-gold hover:bg-brand-gold hover:text-white transition-colors flex items-center justify-center gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Text Rivky
                </a>
              </div>
              {/* A failed fetch tells us nothing about capacity — don't
                  ask the customer to join a waitlist for a day that may
                  well have open slots. */}
              {!slotsError && (
                <WaitlistCard
                  name={name}
                  email={email}
                  phone={phone}
                  date={date}
                  appointmentType={appointmentType}
                  i18nNamespace={i18nNamespace}
                />
              )}
            </div>
          ) : (
            <div className="border border-gray-100 bg-white p-4 sm:p-5">
              <p className="text-xs text-brand-muted mb-3">
                {formatDisplayDate(date)}
              </p>
              <div className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 gap-2">
                {availableSlots.map((slot) => {
                  const isSelected = time === slot.time;
                  return (
                    <button
                      key={slot.time}
                      onClick={() => {
                        if (slot.available) {
                          setTakenNotice(null);
                          setTime(slot.time);
                          trackEvent(trackTimeEventName, {
                            time: slot.time,
                            date,
                          });
                          // Fire the 10-min hold. Best-effort; if
                          // capacity is gone we toast + reset the picker.
                          slotReservation
                            .reserve({
                              date,
                              startTime: slot.time,
                              type: appointmentType,
                            })
                            .catch((err: unknown) => {
                              const msg =
                                err instanceof Error && err.message
                                  ? err.message
                                  : "Couldn't hold this slot";
                              toast(msg, "error");
                              setTakenNotice(t("slotTakenNotice"));
                              setTime("");
                              // Refresh so the taken time drops off the grid.
                              fetchSlots(date, appointmentType, additionalKey);
                            });
                        }
                      }}
                      disabled={!slot.available}
                      className={cn(
                        "px-3 py-3 rounded-full text-sm font-medium border transition-all duration-200",
                        isSelected
                          ? "bg-brand-gold text-white border-brand-gold shadow-md"
                          : slot.available
                            ? "bg-white text-brand-dark border-gray-200 hover:border-brand-gold hover:text-brand-gold"
                            : "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed line-through"
                      )}
                    >
                      {slot.time}
                    </button>
                  );
                })}
              </div>
              {/* Booked times are left off the grid entirely, so a
                  customer looking for 11:00 just doesn't see it. Say so,
                  so a missing hour reads as "taken", not "broken". */}
              <p className="mt-4 flex items-start gap-1.5 text-xs text-brand-muted">
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-brand-gold" />
                {t("missingTimesHint")}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* "Need a time not shown?" fallback — perpetual helper
          at the bottom of the datetime step so customers have
          an escape hatch before they hit a dead end with
          closed Shabbos/YT days or full slots. */}
      <div className="mt-6 rounded-lg border border-gray-100 bg-brand-cream/30 p-4 sm:p-5">
        <p className="text-sm font-medium text-brand-dark mb-1">
          Need a date or time not shown here?
        </p>
        <p className="text-xs text-brand-muted mb-3">
          Message or call Rivky directly — she can often accommodate special
          requests.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <a
            href={phoneToTelHref(voicePhone)}
            className="flex-1 rounded-lg border border-brand-gold/60 px-4 py-2.5 text-sm font-medium text-brand-gold hover:bg-brand-gold hover:text-white transition-colors flex items-center justify-center gap-2"
          >
            <Phone className="h-4 w-4" />
            Call {voicePhone}
          </a>
          <a
            href={phoneToSmsHref(
              supportPhone,
              `Hi Rivky, I'd like to book an appointment but don't see a time that works. Can you accommodate me?`
            )}
            className="flex-1 rounded-lg border border-brand-gold/60 px-4 py-2.5 text-sm font-medium text-brand-gold hover:bg-brand-gold hover:text-white transition-colors flex items-center justify-center gap-2"
          >
            <Mail className="h-4 w-4" />
            Text Rivky
          </a>
        </div>
      </div>
    </div>
  );
}
